import { Link } from 'react-router-dom';
import { StatCard } from './StatCard';
import { SectionCard } from './SectionCard';
import { EmptyState } from './EmptyState';
import { useDashboardData } from '../hooks/useDashboardData';
import {
  Building2,
  Users,
  CreditCard,
  PhoneCall,
  ArrowUpRight,
  Globe,
  Loader2
} from 'lucide-react';

export function SuperAdminDashboard() {
  const { summary, isSummaryLoading } = useDashboardData();

  const pStats = summary?.platformStats;

  const platformStats = [
    { label: 'Total Organizations', value: pStats?.totalOrganizations ?? '-', sub: 'Registered workspaces', icon: Building2, badgeText: '-', badgeColor: 'var(--admin-card-subtext)' },
    { label: 'Total Users', value: pStats?.totalUsers ?? '-', sub: 'Across all orgs', icon: Users, badgeText: '-', badgeColor: 'var(--admin-card-subtext)' },
    { label: 'Active Subscriptions', value: pStats?.activeSubscriptions ?? '-', sub: 'Paying plans', icon: CreditCard, badgeText: '-', badgeColor: 'var(--admin-card-subtext)' },
    { label: 'Calls Processed', value: pStats?.callsProcessed ?? '-', sub: 'Platform-wide total', icon: PhoneCall, badgeText: '-', badgeColor: 'var(--admin-card-subtext)' },
  ];

  const recentOrgs = summary?.recentOrgs || [];
  const planDist = summary?.planDistribution || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div>
          <h1 style={{ color: 'var(--admin-card-text)', margin: 0, fontFamily: 'var(--admin-font-primary)', fontWeight: 700, fontStyle: 'normal', fontSize: '37.7px', lineHeight: '38px', letterSpacing: '-0.76px', verticalAlign: 'middle' }}>
            Platform Overview
          </h1>
          <p className="text-xs font-normal" style={{ color: 'var(--admin-card-subtext)', margin: '8px 0 0' }}>
            Super Admin - full platform visibility.
          </p>
        </div>
      </div>

      {isSummaryLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Loader2 className="animate-spin" size={32} style={{ color: 'var(--admin-card-subtext)' }} />
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {platformStats.map((s, i) => <StatCard key={s.label} {...s} index={i} />)}
          </div>

      {/* Recent Organizations + Plan Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px' }}>
        <SectionCard title="Recently Registered Organizations" icon={Building2} index={4}>
          {recentOrgs.length === 0 ? (
            <EmptyState icon={Building2} title="No organizations yet" desc="Organizations that sign up will appear here with their plan and member count." />
          ) : (
            recentOrgs.map((org:any, idx:number) => (
              <div key={idx} style={{ padding: '12px 16px', borderBottom: '1px solid var(--admin-card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: 'var(--color-glass-surface, rgba(255,255,255,0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Building2 size={14} color="var(--admin-card-text)" />
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', margin: 0 }}>{org.name}</p>
                    <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: '2px 0 0' }}>{org.memberCount} members · {org.plan}</p>
                  </div>
                </div>
                <ArrowUpRight size={14} color="var(--admin-card-text)" />
              </div>
            ))
          )}
        </SectionCard>

        <SectionCard title="Plan Distribution" icon={CreditCard} index={5}>
          {(['freemium', 'solo', 'closer', 'team'] as const).map((plan, idx) => {
            const colors: Record<string, string> = {
              freemium: 'var(--color-text-secondary, #94a3b8)',
              solo: 'var(--color-status-sky, #38bdf8)',
              closer: 'var(--color-success, #10B981)',
              team: '#F59E0B',
            };
            return (
              <div key={plan} style={{ padding: '12px 16px', borderBottom: idx < 3 ? '1px solid var(--admin-card-border)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors[plan], flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', textTransform: 'capitalize' }}>{plan}</span>
                </div>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--admin-card-subtext)' }}>{planDist[plan] ?? 0}</span>
                </div>
              );
            })}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--admin-card-border)' }}>
              <Link to="/admin/organizations" style={{ fontSize: '12px', fontWeight: 700, color: 'var(--admin-card-text)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Globe size={12} /> View all organizations
              </Link>
            </div>
          </SectionCard>
        </div>
        </>
      )}
    </div>
  );
}
